import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { db, storage } from '../../../firebase';
import { collection, query, where, getDocs, getDocsFromServer, onSnapshot, doc, updateDoc, addDoc, Timestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import ClientLayout from '../../../components/layout/ClientLayout';
import { requestPortvilleContractCancellation } from '../../../services/PortvilleContractCancellationService';

interface PortvilleRoom {
  id: string;
  roomNumber: string;
  status: 'occupied' | 'preparing' | 'vacant' | 'contracting';
}

interface UserProfile {
  name: string;
  phoneNumber: string;
  organization: string;
}

interface LotteryStatus {
  hasLottery: boolean;
  status: 'drawn' | 'contracted' | null;
  roomNumber: string;
  roomId: string;
  lotteryDocId?: string;
  documents?: LotteryDocuments;
  cancellationRequestStatus?: 'pending' | 'rejected' | 'approved' | 'cancelled';
  cancellationReason?: string;
}

type LotteryDocumentType = 'identification' | 'employment' | 'other';

type LotteryDocuments = Partial<Record<LotteryDocumentType, string>>;

const getDocumentDisplayName = (docType: LotteryDocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

const LotteryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading: emailVerificationLoading } = useEmailVerification();
  const [availableRooms, setAvailableRooms] = useState<PortvilleRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<PortvilleRoom | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [uploadingDocType, setUploadingDocType] = useState<LotteryDocumentType | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [cancellationError, setCancellationError] = useState<string | null>(null);
  const [isRequestingCancellation, setIsRequestingCancellation] = useState(false);
  const [isInspectionCompleted, setIsInspectionCompleted] = useState(false);
  const [inspectionCompletedAt, setInspectionCompletedAt] = useState<Date | null>(null);
  const [applicationEligibilityLoading, setApplicationEligibilityLoading] = useState(true);
  const [hasApprovedApplication, setHasApprovedApplication] = useState(false);
  const [applicationEligibilityError, setApplicationEligibilityError] = useState<string | null>(null);
  const [lotteryStatus, setLotteryStatus] = useState<LotteryStatus>({
    hasLottery: false,
    status: null,
    roomNumber: '',
    roomId: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadUserProfile();
      loadAvailableRooms();
      checkExistingLottery();
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setHasApprovedApplication(false);
      setApplicationEligibilityLoading(false);
      return;
    }

    setApplicationEligibilityLoading(true);
    setApplicationEligibilityError(null);

    const applicationsQuery = query(
      collection(db, 'portville_applications'),
      where('uid', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      applicationsQuery,
      { includeMetadataChanges: true },
      (snapshot) => {
        // 캐시 결과만으로 추첨 자격을 열지 않고 서버 확인이 끝난 결과만 사용한다.
        if (snapshot.metadata.fromCache) return;

        const hasApproved = snapshot.docs.some((applicationDoc) => (
          applicationDoc.data()?.status === 'approved'
        ));
        setHasApprovedApplication(hasApproved);
        setApplicationEligibilityLoading(false);
        setApplicationEligibilityError(null);
      },
      (eligibilityError) => {
        console.error('포트빌 추첨 자격 확인 실패:', eligibilityError);
        setHasApprovedApplication(false);
        setApplicationEligibilityLoading(false);
        setApplicationEligibilityError('추첨 자격을 확인할 수 없습니다. 잠시 후 다시 시도해주세요.');
      }
    );

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const loadInspectionStatus = async () => {
      if (!user) return;
      if (!lotteryStatus.hasLottery) return;
      if (!lotteryStatus.roomId) return;

      try {
        setIsInspectionCompleted(false);
        setInspectionCompletedAt(null);

        const roomDoc = await getDoc(doc(db, 'portville_rooms', lotteryStatus.roomId));
        const roomData = roomDoc.exists() ? (roomDoc.data() as any) : null;
        const residencyId = roomData?.currentResidencyId;
        if (!residencyId) return;

        const residencyDoc = await getDoc(doc(db, 'portville_residencies', residencyId));
        const residencyData = residencyDoc.exists() ? (residencyDoc.data() as any) : null;
        if (!residencyData) return;
        if (String(residencyData.residentUid || '') !== String(user.uid)) {
          // 다른 사용자의 currentResidencyId가 연결되어 있을 수 있으므로, 본인 residency가 아닌 경우 잠금 판단을 하지 않는다.
          return;
        }
        const signedAt = residencyData?.moveInInspectionSignedAt?.toDate?.();
        if (signedAt) {
          setIsInspectionCompleted(true);
          setInspectionCompletedAt(signedAt);
        }
      } catch (inspectionError) {
        console.warn('입주점검 상태 조회 실패:', inspectionError);
      }
    };

    loadInspectionStatus();
  }, [user, lotteryStatus.hasLottery, lotteryStatus.roomId]);

  const loadUserProfile = async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'user_profiles', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserProfile({
          name: data.name || '',
          phoneNumber: data.phoneNumber || '',
          organization: data.organization || ''
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadAvailableRooms = async () => {
    try {
      setLoading(true);
      const roomsRef = collection(db, 'portville_rooms');
      const q = query(roomsRef, where('status', '==', 'vacant'));
      const querySnapshot = await getDocs(q);
      
      const rooms: PortvilleRoom[] = [];
      querySnapshot.forEach((doc) => {
        rooms.push({ ...(doc.data() as any), id: doc.id } as PortvilleRoom);
      });
      
      // 호실번호 순으로 정렬
      rooms.sort((a, b) => a.roomNumber.localeCompare(b.roomNumber));
      setAvailableRooms(rooms);
    } catch (error) {
      console.error('Error loading available rooms:', error);
      setError('호실 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingLottery = async () => {
    if (!user) return;
    
    try {
      const getMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value.toMillis === 'function') return value.toMillis();
          if (typeof value.toDate === 'function') return value.toDate().getTime();
          if (value instanceof Date) return value.getTime();
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? 0 : date.getTime();
        } catch {
          return 0;
        }
      };

      // 1) 진행중(in-progress) 입주/계약이 있으면 LotteryPage를 사용할 수 없다.
      const residenciesRef = collection(db, 'portville_residencies');
      const residencyQuery = query(residenciesRef, where('residentUid', '==', user.uid));
      const residencySnapshot = await getDocs(residencyQuery);
      const residencyDocs = !residencySnapshot.empty
        ? residencySnapshot.docs.map((snap) => snap.data() as any)
        : [];

      // 2) terminal anchor: updatedAt은 관리자 수정으로 흔들릴 수 있으므로 제외
      const terminalAnchorMillis = residencyDocs
        .filter((r) => r?.status === 'completed' || r?.status === 'terminated')
        .reduce((acc, r) => {
          const key =
            getMillis(r?.completedAt) ||
            getMillis(r?.terminatedAt) ||
            getMillis(r?.contractCancelledAt) ||
            getMillis(r?.contractSignedAt);
          return Math.max(acc, key);
        }, 0);

      const getResidencyCycleMillis = (r: any) =>
        getMillis(r?.updatedAt) || getMillis(r?.contractSignedAt) || getMillis(r?.createdAt);

      const currentResidency = residencyDocs
        .filter((r) => (
          (r?.status === 'active' || r?.status === 'contracting') &&
          getResidencyCycleMillis(r) > terminalAnchorMillis
        ))
        .sort((a, b) => getResidencyCycleMillis(b) - getResidencyCycleMillis(a))[0];

      if (currentResidency?.status === 'active' || currentResidency?.contractSignedAt) {
        navigate('/portville/residency');
        return;
      }

      if (currentResidency?.status === 'contracting') {
        navigate('/portville/contract');
        return;
      }

      const lotteryRef = collection(db, 'portville_lottery_history');
      const q = query(lotteryRef, where('uid', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setLotteryStatus({ hasLottery: false, status: null, roomNumber: '', roomId: '' });
        setSelectedRoom(null);
        setHasDrawn(false);
        return;
      }

      // NOTE(policy): "현재 진행" lottery는 status='drawn' && !archivedAt && !suppressedAt 인 문서만 인정한다.
      // NOTE(policy): 최신/새 회차 기준은 drawnAt 단일로 통일한다.
      const candidates = querySnapshot.docs
        .map((snap) => ({ id: snap.id, data: snap.data() as any }))
        .filter((d) => {
          const data = d.data;
          if (data?.status !== 'drawn') return false;
          if (data?.archivedAt) return false;
          if (data?.suppressedAt) return false;
          const drawnAtMillis = getMillis(data?.drawnAt);
          return drawnAtMillis > terminalAnchorMillis;
        })
        .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

      const latest = candidates[0] ?? null;
      if (!latest) {
        setLotteryStatus({ hasLottery: false, status: null, roomNumber: '', roomId: '' });
        setSelectedRoom(null);
        setHasDrawn(false);
        return;
      }

      const existingLottery = latest.data;
      setLotteryStatus({
        hasLottery: true,
        status: 'drawn',
        roomNumber: existingLottery.roomNumber,
        roomId: existingLottery.roomId,
        lotteryDocId: latest.id,
        documents: (existingLottery.documents || {}) as LotteryDocuments,
        cancellationRequestStatus: existingLottery.cancellationRequestStatus,
        cancellationReason: existingLottery.cancellationReason
      });
      setSelectedRoom({
        id: existingLottery.roomId,
        roomNumber: existingLottery.roomNumber,
        status: 'contracting'
      } as PortvilleRoom);
      setHasDrawn(true);
    } catch (error) {
      console.error('Error checking existing lottery:', error);
    }
  };

  const validateUploadFile = (file: File): string | null => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      return `파일 크기는 5MB를 초과할 수 없습니다. (현재: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return `허용되지 않는 파일 형식입니다. PDF, JPG, PNG 파일만 업로드할 수 있습니다. (현재: ${file.type})`;
    }

    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return '허용되지 않는 파일 확장자입니다. .pdf, .jpg, .jpeg, .png 파일만 업로드할 수 있습니다.';
    }

    return null;
  };

  const uploadDocument = async (type: LotteryDocumentType, file: File) => {
    if (!user) return;
    if (!lotteryStatus.lotteryDocId) {
      setDocumentError('추첨 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    if (isInspectionCompleted) {
      setDocumentError('입주점검이 완료된 이후에는 서류를 추가 업로드/변경할 수 없습니다.');
      return;
    }

    const validationError = validateUploadFile(file);
    if (validationError) {
      setDocumentError(validationError);
      return;
    }

    const existingPath = lotteryStatus.documents?.[type];
    try {
      setUploadingDocType(type);
      setDocumentError(null);

      const timestamp = Date.now();
      const safeOriginalName = file.name.split('/').join('_');
      const fileName = `${user.uid}_${type}_${timestamp}_${safeOriginalName}`;
      const storagePath = `portville_lottery_documents/${user.uid}/${lotteryStatus.lotteryDocId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      try {
        // 1) 새 파일 업로드
        await uploadBytes(storageRef, file, { contentType: file.type });

        // 2) Firestore에 새 경로 반영 (성공해야만 이전 파일을 지움)
        const updatedDocuments: LotteryDocuments = {
          ...(lotteryStatus.documents || {}),
          [type]: storagePath
        };

        await updateDoc(doc(db, 'portville_lottery_history', lotteryStatus.lotteryDocId), {
          documents: updatedDocuments,
          documentsUpdatedAt: Timestamp.now()
        });

        setLotteryStatus((prev) => ({
          ...prev,
          documents: updatedDocuments
        }));

        // 3) 기존 파일 정리 (best effort)
        if (existingPath && existingPath !== storagePath) {
          try {
            await deleteObject(ref(storage, existingPath));
          } catch (deleteError) {
            console.warn('기존 파일 삭제 실패:', deleteError);
            setDocumentError('파일은 업로드되었지만, 기존 파일 삭제에 실패했습니다. 관리자에게 문의해주세요.');
          }
        }
      } catch (innerError) {
        // Firestore 반영 실패/업로드 실패 시 새로 업로드된 파일 정리 (best effort)
        try {
          await deleteObject(storageRef);
        } catch (cleanupError) {
          console.warn('업로드 실패 후 새 파일 정리 실패:', cleanupError);
        }
        throw innerError;
      }
    } catch (uploadError) {
      console.error('서류 업로드 실패:', uploadError);
      setDocumentError('서류 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setUploadingDocType(null);
    }
  };

  const deleteOptionalDocument = async (type: LotteryDocumentType) => {
    if (!user) return;
    if (!lotteryStatus.lotteryDocId) return;
    if (type !== 'other') return;
    if (isInspectionCompleted) {
      setDocumentError('입주점검이 완료된 이후에는 서류를 삭제할 수 없습니다.');
      return;
    }

    const existingPath = lotteryStatus.documents?.[type];
    if (!existingPath) return;

    try {
      setUploadingDocType(type);
      setDocumentError(null);

      const updatedDocuments: LotteryDocuments = { ...(lotteryStatus.documents || {}) };
      delete updatedDocuments[type];

      await updateDoc(doc(db, 'portville_lottery_history', lotteryStatus.lotteryDocId), {
        documents: updatedDocuments,
        documentsUpdatedAt: Timestamp.now()
      });

      setLotteryStatus((prev) => ({
        ...prev,
        documents: updatedDocuments
      }));

      // Firestore 반영 후 Storage 파일 삭제 (best effort)
      try {
        await deleteObject(ref(storage, existingPath));
      } catch (deleteError) {
        console.warn('기존 파일 삭제 실패:', deleteError);
        setDocumentError('서류는 목록에서 제거되었지만, Storage 파일 삭제에 실패했습니다. 관리자에게 문의해주세요.');
      }
    } catch (deleteError) {
      console.error('서류 삭제 실패:', deleteError);
      setDocumentError('서류 삭제 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleDraw = async () => {
    if (availableRooms.length === 0) {
      setError('현재 이용 가능한 호실이 없습니다.');
      return;
    }

    if (lotteryStatus.hasLottery) {
      setError('이미 호실을 추첨받으셨습니다.');
      return;
    }

    setIsDrawing(true);
    setError(null);
    setApplicationEligibilityError(null);

    const verifyApprovedApplication = async () => {
      if (!user) return false;

      const applicationsQuery = query(
        collection(db, 'portville_applications'),
        where('uid', '==', user.uid)
      );
      const snapshot = await getDocsFromServer(applicationsQuery);
      return snapshot.docs.some((applicationDoc) => (
        applicationDoc.data()?.status === 'approved'
      ));
    };

    try {
      const isApproved = await verifyApprovedApplication();
      if (!isApproved) {
        setHasApprovedApplication(false);
        setIsDrawing(false);
        return;
      }
    } catch (eligibilityError) {
      console.error('추첨 실행 전 자격 확인 실패:', eligibilityError);
      setHasApprovedApplication(false);
      setApplicationEligibilityError('추첨 자격을 확인할 수 없습니다. 잠시 후 다시 시도해주세요.');
      setIsDrawing(false);
      return;
    }

    // 추첨 애니메이션 (2초간)
    const drawAnimation = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * availableRooms.length);
      setSelectedRoom(availableRooms[randomIndex]);
    }, 100);

    setTimeout(async () => {
      clearInterval(drawAnimation);
      
      // 최종 호실 선택
      const finalIndex = Math.floor(Math.random() * availableRooms.length);
      const finalRoom = availableRooms[finalIndex];
      setSelectedRoom(finalRoom);

      // 호실 상태를 'contracting'으로 변경하고 사용자 정보 저장
      try {
        // 애니메이션 도중 관리자가 신청 상태를 변경했을 수 있으므로 저장 직전에 다시 검증한다.
        let isStillApproved = false;
        try {
          isStillApproved = await verifyApprovedApplication();
        } catch (eligibilityError) {
          console.error('호실 배정 직전 자격 확인 실패:', eligibilityError);
          setHasApprovedApplication(false);
          setApplicationEligibilityError('추첨 자격을 확인할 수 없습니다. 잠시 후 다시 시도해주세요.');
          setSelectedRoom(null);
          return;
        }

        if (!isStillApproved) {
          setHasApprovedApplication(false);
          setApplicationEligibilityError(null);
          setSelectedRoom(null);
          return;
        }

        let residentProfile = userProfile;
        if (!residentProfile && user) {
          try {
            const userDoc = await getDoc(doc(db, 'user_profiles', user.uid));
            if (userDoc.exists()) {
              const data = userDoc.data() as any;
              residentProfile = {
                name: data.name || '',
                phoneNumber: data.phoneNumber || '',
                organization: data.organization || ''
              };
            }
          } catch (profileError) {
            console.warn('추첨 시 사용자 프로필 조회 실패:', profileError);
          }
        }

        await updateDoc(doc(db, 'portville_rooms', finalRoom.id), {
          status: 'contracting',
          currentResident: {
            name: residentProfile?.name || '',
            phoneNumber: residentProfile?.phoneNumber || '',
            organization: residentProfile?.organization || ''
          },
          residentUid: user?.uid || '',
          updatedAt: Timestamp.now()
        });

        // 추첨 기록 저장
        const lotteryDocRef = await addDoc(collection(db, 'portville_lottery_history'), {
          uid: user?.uid,
          roomId: finalRoom.id,
          roomNumber: finalRoom.roomNumber,
          drawnAt: Timestamp.now(),
          status: 'drawn',
          documents: {}
        });

        // 로컬 상태 업데이트
        setLotteryStatus({
          hasLottery: true,
          status: 'drawn',
          roomNumber: finalRoom.roomNumber,
          roomId: finalRoom.id,
          lotteryDocId: lotteryDocRef.id,
          documents: {}
        });
        setHasDrawn(true);

      } catch (error) {
        console.error('Error updating room status:', error);
        setError('호실 배정 중 오류가 발생했습니다.');
      } finally {
        setIsDrawing(false);
      }
    }, 2000);
  };

  const handleProceedToContract = () => {
    const documents = lotteryStatus.documents || {};
    const hasRequiredDocuments = Boolean(documents.identification && documents.employment);
    if (lotteryStatus.cancellationRequestStatus === 'pending') {
      setCancellationError('계약 포기 신청 검토 중에는 계약을 진행할 수 없습니다.');
      return;
    }

    if (lotteryStatus.status === 'drawn' && !hasRequiredDocuments) {
      setDocumentError('필수 서류(신분증 사본, 재직증명서/사업자등록증)를 업로드한 후 계약을 진행할 수 있습니다.');
      return;
    }
    navigate('/portville/contract');
  };

  const handleRequestCancellation = async () => {
    if (!lotteryStatus.lotteryDocId && !lotteryStatus.roomId) {
      setCancellationError('추첨 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    if (lotteryStatus.cancellationRequestStatus === 'pending') {
      setCancellationError('이미 계약 포기 신청이 접수되어 관리자 검토 중입니다.');
      return;
    }

    const reason = window.prompt('계약 포기 신청 사유를 입력해주세요. 관리자 승인 후 최종 포기 처리됩니다.');
    if (reason === null) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setCancellationError('계약 포기 신청 사유를 입력해주세요.');
      return;
    }

    const confirmed = window.confirm(
      `${lotteryStatus.roomNumber}호 계약 포기 신청을 제출하시겠습니까?\n\n제출 후 관리자 승인 전까지 계약 진행이 제한됩니다.`
    );
    if (!confirmed) return;

    try {
      setIsRequestingCancellation(true);
      setCancellationError(null);
      await requestPortvilleContractCancellation({
        lotteryId: lotteryStatus.lotteryDocId,
        roomId: lotteryStatus.roomId,
        cancellationReason: trimmedReason
      });

      setLotteryStatus((prev) => ({
        ...prev,
        cancellationRequestStatus: 'pending',
        cancellationReason: trimmedReason
      }));
      alert('계약 포기 신청이 접수되었습니다. 관리자 검토 후 최종 처리됩니다.');
    } catch (error: any) {
      console.error('계약 포기 신청 실패:', error);
      const message = error?.message || '계약 포기 신청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      setCancellationError(message);
    } finally {
      setIsRequestingCancellation(false);
    }
  };

  if (authLoading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!user) {
    return null;
  }

  const documents = lotteryStatus.documents || {};
  const hasRequiredDocuments = Boolean(documents.identification && documents.employment);
  const hasCancellationPending = lotteryStatus.cancellationRequestStatus === 'pending';
  const hasCancellationRejected = lotteryStatus.cancellationRequestStatus === 'rejected';
  const canRequestCancellation = !hasCancellationPending;

  return (
    <ClientLayout>
      <div className="flex flex-col min-h-screen bg-white">
        <div className="flex-1 flex justify-center py-8 sm:py-12 md:py-[115px]">
          <div className="w-full max-w-[750px] px-4 sm:px-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 font-['Pretendard'] leading-9 mb-4">
              포트빌 호실 추첨
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-normal text-slate-700 font-['Pretendard'] leading-9 mb-8">
              입주 가능한 호실을 추첨을 통해 배정받으세요
            </p>
            <div className="w-full h-px bg-gray-200 mb-8"></div>

            {loading || applicationEligibilityLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : error || applicationEligibilityError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <p className="text-red-800 text-center">{error || applicationEligibilityError}</p>
              </div>
            ) : (
              <>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 font-['Pretendard'] leading-9 mb-4">
                  이용 가능한 호실 현황
                </h2>
                
                <div className="bg-gray-50 rounded border border-black/5 p-8 sm:p-12 mb-8">
                  {lotteryStatus.hasLottery && lotteryStatus.status === 'contracted' ? (
                    <div className="text-center">
                      <div className="mb-4">
                        <svg 
                          className="w-16 h-16 text-green-500 mx-auto mb-4" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M9 12L11 14L15 10" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <circle 
                            cx="12" 
                            cy="12" 
                            r="9" 
                            stroke="currentColor" 
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                      <p className="text-green-800 font-bold text-lg mb-2">계약이 완료되었습니다!</p>
                      <p className="text-green-700 mb-4">
                        배정 호실: <span className="font-bold">{lotteryStatus.roomNumber}호</span>
                      </p>
                      <p className="text-sm text-green-600">
                        계약 정보는 입주 계약 메뉴에서 확인하실 수 있습니다.
                      </p>
                    </div>
                  ) : lotteryStatus.hasLottery && lotteryStatus.status === 'drawn' ? (
                    <div className="text-center">
                      <div className="mb-4">
                        <svg 
                          className="w-16 h-16 text-yellow-500 mx-auto mb-4" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M12 8V12L15 15" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <circle 
                            cx="12" 
                            cy="12" 
                            r="9" 
                            stroke="currentColor" 
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                      <p className="text-yellow-800 font-bold text-lg mb-2">추첨이 완료되었습니다!</p>
                      <p className="text-yellow-700 mb-4">
                        배정 호실: <span className="font-bold">{lotteryStatus.roomNumber}호</span>
                      </p>
	                      <p className="text-sm text-yellow-600">
	                        {hasCancellationPending
	                          ? '계약 포기 신청이 접수되어 관리자 검토 중입니다.'
	                          : '계약 진행 전, 필수 서류를 업로드해주세요.'}
	                      </p>
	                    </div>
                  ) : availableRooms.length === 0 ? (
                    <p className="text-center text-gray-500">현재 이용 가능한 호실이 없습니다.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-8">
                        {availableRooms.map((room) => (
                          <div
                            key={room.id}
                            className={`p-4 text-center rounded-sm outline outline-1 outline-slate-200 transition-colors ${
                              selectedRoom?.id === room.id && hasDrawn
                                ? 'bg-blue-100 outline-blue-300'
                                : selectedRoom?.id === room.id && isDrawing
                                ? 'bg-yellow-100 outline-yellow-300'
                                : 'bg-white'
                            }`}
                          >
                            <span className="text-lg font-semibold text-slate-800 font-['Pretendard']">
                              {room.roomNumber}호
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="text-center mb-8">
                        <p className="text-base text-slate-600 font-['Pretendard'] mb-2">
                          총 <span className="font-bold text-blue-600">{availableRooms.length}개</span>의 호실이 이용 가능합니다
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {lotteryStatus.hasLottery && lotteryStatus.status === 'drawn' ? (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                      <h3 className="text-xl font-bold text-blue-800 font-['Pretendard'] mb-2">
                        축하합니다!
                      </h3>
                      <p className="text-blue-700 font-['Pretendard'] mb-4">
                        <span className="text-2xl font-bold">{lotteryStatus.roomNumber}호</span>가 배정되었습니다
                      </p>
	                      <p className="text-sm text-blue-600 font-['Pretendard']">
	                        {hasCancellationPending
	                          ? '계약 포기 신청이 접수되어 관리자 검토 중입니다'
	                          : '계약 진행 전, 서류 업로드가 필요합니다'}
	                      </p>
	                    </div>

	                    {hasCancellationPending && (
	                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
	                        <p className="text-red-800 font-bold text-center mb-1">계약 포기 신청 검토 중</p>
	                        <p className="text-red-700 text-sm text-center">
	                          관리자 승인 전까지 계약 진행이 제한됩니다.
	                        </p>
	                        {lotteryStatus.cancellationReason && (
	                          <p className="text-red-700 text-sm text-center mt-2">
	                            신청 사유: {lotteryStatus.cancellationReason}
	                          </p>
	                        )}
	                      </div>
	                    )}

	                    {hasCancellationRejected && (
	                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
	                        <p className="text-yellow-900 font-bold text-center mb-1">이전 계약 포기 신청 반려</p>
	                        <p className="text-yellow-800 text-sm text-center">
	                          필요 시 계약 포기 신청을 다시 제출할 수 있습니다.
	                        </p>
	                      </div>
	                    )}

	                    {cancellationError && (
	                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
	                        <p className="text-red-800 text-sm text-center">{cancellationError}</p>
	                      </div>
	                    )}

                    <div className="bg-white rounded-lg border border-slate-200 p-6">
                      <h3 className="text-lg font-bold text-slate-800 font-['Pretendard'] mb-2">
                        계약 전 서류 업로드
                      </h3>
                      <p className="text-sm text-slate-600 font-['Pretendard'] mb-4">
                        포트빌 신청 시 제출하던 서류와 동일한 종류입니다.
                      </p>

                      {documentError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                          {documentError}
                        </div>
                      )}
                      {isInspectionCompleted && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-900 text-sm">
                          입주점검이 완료되어 서류를 추가 업로드/변경할 수 없습니다.
                          {inspectionCompletedAt && (
                            <span className="ml-1 text-yellow-800">
                              (완료일: {inspectionCompletedAt.toLocaleString('ko-KR')})
                            </span>
                          )}
                        </div>
                      )}

                      <div className="space-y-4">
                        {(['identification', 'employment', 'other'] as const).map((type) => {
                          const storagePath = documents[type];
                          const isRequired = type === 'identification' || type === 'employment';

                          return (
                            <div key={type} className="flex flex-col gap-2">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <span className="text-slate-800 font-medium font-['Pretendard']">
                                  {getDocumentDisplayName(type)}
                                  {isRequired && <span className="ml-2 text-xs text-red-500">(필수)</span>}
                                </span>
                                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                                  <input
                                    type="file"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      e.target.value = '';
                                      if (file) uploadDocument(type, file);
                                    }}
                                    className="hidden"
                                    id={`lottery-file-${type}`}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    disabled={uploadingDocType !== null || isInspectionCompleted}
                                  />
                                  <label
                                    htmlFor={`lottery-file-${type}`}
                                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded transition-colors cursor-pointer text-sm md:text-base whitespace-nowrap ${
                                      uploadingDocType !== null || isInspectionCompleted
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {uploadingDocType === type
                                      ? '업로드 중...'
                                      : isInspectionCompleted
                                        ? '업로드 불가'
                                        : storagePath
                                          ? '파일 변경'
                                          : '파일 선택'}
                                  </label>
                                  {type === 'other' && storagePath && (
                                    <button
                                      onClick={() => deleteOptionalDocument('other')}
                                      disabled={uploadingDocType !== null || isInspectionCompleted}
                                      className={`px-3 md:px-4 py-1.5 md:py-2 rounded transition-colors text-sm md:text-base whitespace-nowrap ${
                                        uploadingDocType !== null || isInspectionCompleted
                                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                                      }`}
                                    >
                                      삭제
                                    </button>
                                  )}
                                </div>
                              </div>

                              {storagePath ? (
                                <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                                  업로드 완료: {getOriginalFileNameFromStoragePath(storagePath)}
                                </div>
                              ) : (
                                <div className="text-sm text-slate-500">아직 업로드되지 않았습니다.</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-4">
	                      <button
	                        onClick={handleProceedToContract}
	                        disabled={!hasRequiredDocuments || uploadingDocType !== null || hasCancellationPending}
	                        className={`w-full h-14 sm:h-16 rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9 ${
	                          !hasRequiredDocuments || uploadingDocType !== null || hasCancellationPending
	                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
	                            : 'bg-blue-700 text-white hover:bg-blue-800 focus:ring-blue-500'
	                        }`}
	                      >
	                        입주 계약 진행하기
	                      </button>
	                      <button
	                        onClick={handleRequestCancellation}
	                        disabled={isRequestingCancellation || !canRequestCancellation}
	                        className={`w-full h-14 sm:h-16 rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9 ${
	                          isRequestingCancellation || !canRequestCancellation
	                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
	                            : 'bg-white text-red-700 border border-red-200 hover:bg-red-50 focus:ring-red-500'
	                        }`}
	                      >
	                        {hasCancellationPending
	                          ? '계약 포기 신청 검토 중'
	                            : isRequestingCancellation
	                              ? '계약 포기 신청 중...'
		                            : hasCancellationRejected
		                              ? '계약 포기 재신청'
		                              : '계약 포기 신청'}
	                      </button>
	                      {!hasRequiredDocuments && (
	                        <p className="text-sm text-slate-600 text-center font-['Pretendard']">
	                          필수 서류 업로드 후 계약 진행이 가능합니다.
                        </p>
                      )}
                      <button
                        onClick={() => navigate('/portville')}
                        className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                      >
                        포트빌 메인으로 돌아가기
                      </button>
                    </div>
                  </div>
                ) : lotteryStatus.hasLottery ? (
                  <div className="space-y-4">
                    {lotteryStatus.status === 'contracted' && (
                      <button
                        onClick={handleProceedToContract}
                        className="w-full h-14 sm:h-16 bg-green-700 text-white rounded-sm hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                      >
                        계약 정보 확인하기
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/portville')}
                      className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                    >
                      포트빌 메인으로 돌아가기
                    </button>
                  </div>
                ) : !hasApprovedApplication ? (
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-900 text-center font-medium">
                        관리자가 입주진행으로 승인한 신청서가 있어야 호실을 추첨할 수 있습니다.
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/portville')}
                      className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                    >
                      포트빌 메인으로 돌아가기
                    </button>
                  </div>
                ) : (
                  availableRooms.length > 0 && (
                    <div className="space-y-4">
                      <button
                        onClick={handleDraw}
                        disabled={isDrawing || lotteryStatus.hasLottery}
                        className={`w-full h-16 sm:h-20 rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-lg sm:text-xl font-bold font-['Pretendard'] leading-9 ${
                          isDrawing
                            ? 'bg-yellow-500 text-white cursor-not-allowed'
                            : lotteryStatus.hasLottery
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-700 text-white hover:bg-blue-800 focus:ring-blue-500'
                        }`}
                      >
                        {isDrawing ? '추첨 중...' : lotteryStatus.hasLottery ? '이미 추첨 완료' : '호실 추첨하기'}
                      </button>
                      <button
                        onClick={() => navigate('/portville')}
                        className="w-full h-14 sm:h-16 bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-base sm:text-lg font-bold font-['Pretendard'] leading-9"
                      >
                        뒤로 가기
                      </button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default LotteryPage; 
